import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import type { Event, JsonPatchOperation } from '../api/generated/model';
import Select from './common/Select';
import { useAllUsers } from '../hooks/useAllUsers';

// sling_shift_time is deliberately absent: it is derived from the assigned
// shifts, so there is nothing for a hand-typed value to be stored in.
type EditableField =
  | 'reviewed'
  | 'tech_setup_required'
  | 'tech_setup_completed'
  | 'pre_program_setup_email_sent'
  | 'pre_program_reminder_email_sent'
  | 'invoiced'
  | 'owner_id'
  | 'internal_notes';

interface OwnerOption {
  value: string;
  label: string;
  active: boolean;
}

interface EventEditModalProps {
  show: boolean;
  onHide: () => void;
  event: Event | null;
  field: EditableField | null;
  onSave: (eventId: string, patch: JsonPatchOperation[]) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
}

const FIELD_LABELS: Record<EditableField, string> = {
  reviewed: 'Reviewed',
  tech_setup_required: 'Tech Setup Required',
  tech_setup_completed: 'Tech Setup Completed',
  pre_program_setup_email_sent: 'Pre-Program Setup Email Sent',
  pre_program_reminder_email_sent: 'Reminder Email Sent',
  invoiced: 'Invoiced',
  owner_id: 'Owner',
  internal_notes: 'Internal Notes',
};

const YES_NO_UNKNOWN_OPTIONS = ['Yes', 'No', 'Unknown'];

const YES_NO_UNKNOWN_FIELDS: EditableField[] = [
  'reviewed',
  'tech_setup_required',
  'tech_setup_completed',
  'pre_program_setup_email_sent',
  'pre_program_reminder_email_sent',
];

const INVOICED_OPTIONS = ['Sent', 'Received', 'Need To Send', 'Unknown'];

const EventEditModal: React.FC<EventEditModalProps> = ({
  show,
  onHide,
  event,
  field,
  onSave,
  isLoading = false,
  error = null,
}) => {
  const [value, setValue] = useState<string>('');
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(null);

  // Initialize value when modal opens or event/field changes
  useEffect(() => {
    if (event && field) {
      const currentValue = event[field];
      setValue(currentValue || '');
    }
  }, [event, field, show]);

  // Set focus back to input when error occurs
  useEffect(() => {
    if (error && inputRef.current) {
      inputRef.current.focus();
    }
  }, [error]);

  const handleSave = async () => {
    if (!event || !field) return;

    // Create JSON Patch operation
    // Always use 'add' operation because Go omits nil pointer fields during JSON marshaling.
    // When a field is nil, it's not present in the JSON, so 'replace' would fail.
    // The 'add' operation works for both adding new fields and replacing existing ones.
    const patch: JsonPatchOperation[] = [
      {
        op: 'add',
        path: `/${field}`,
        value: value === '' ? null : value,
      }
    ];

    try {
      await onSave(event.id!, patch);
      onHide();
    } catch (err) {
      // Error handling is done in the parent component
      console.error('Error saving event:', err);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSave();
  };

  const isYesNoUnknownField = field && YES_NO_UNKNOWN_FIELDS.includes(field);
  const isInvoicedField = field === 'invoiced';
  const isInternalNotesField = field === 'internal_notes';
  const isOwnerField = field === 'owner_id';
  const fieldLabel = field ? FIELD_LABELS[field] : '';

  // Owner picker: fetch the directory only while editing the owner field.
  const { data: allUsers } = useAllUsers({ enabled: show && isOwnerField });

  const ownerOptions = useMemo<OwnerOption[]>(() => {
    const opts: OwnerOption[] = (allUsers || [])
      .filter((u) => u.active && u.id)
      .map((u) => ({
        value: u.id!,
        label: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || u.id!,
        active: true,
      }));

    // Keep the currently-assigned owner selectable even if they're now
    // deactivated (active users are the only *new* assignments, but existing
    // ones must still display). Prepend if not already present.
    const owner = event?.owner;
    if (owner?.id && !opts.some((o) => o.value === owner.id)) {
      const name = `${owner.first_name || ''} ${owner.last_name || ''}`.trim() || owner.email || owner.id;
      opts.unshift({
        value: owner.id,
        label: owner.active ? name : `${name} (deactivated)`,
        active: !!owner.active,
      });
    }
    return opts;
  }, [allUsers, event?.owner]);

  const selectedOwnerOption = ownerOptions.find((o) => o.value === value) || null;

  return (
    <Modal show={show} onHide={onHide} centered onEntered={() => inputRef.current?.focus()}>
      <Modal.Header closeButton>
        <Modal.Title>Edit {fieldLabel}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="mb-3">
            <i className="bi bi-exclamation-triangle me-2"></i>
            <span style={{ whiteSpace: 'pre-line' }}>{error}</span>
          </Alert>
        )}

        {event && field && (
          <Form onSubmit={handleSubmit}>
            <Form.Group>
              <Form.Label>{fieldLabel}</Form.Label>
              {isOwnerField ? (
                <Select<OwnerOption>
                  options={ownerOptions}
                  value={selectedOwnerOption}
                  onChange={(opt) => setValue(opt?.value || '')}
                  isClearable
                  isDisabled={isLoading}
                  placeholder="Search for a user..."
                  blurInputOnSelect
                  autoSize={false}
                  formatOptionLabel={(opt) => (
                    <span className="d-flex align-items-center gap-2">
                      <span>{opt.label}</span>
                      {!opt.active && (
                        <span className="badge bg-secondary">Deactivated</span>
                      )}
                    </span>
                  )}
                />
              ) : isYesNoUnknownField ? (
                <Form.Select
                  ref={inputRef as React.RefObject<HTMLSelectElement>}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  disabled={isLoading}
                >
                  <option value="">-- Select --</option>
                  {YES_NO_UNKNOWN_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Form.Select>
              ) : isInvoicedField ? (
                <Form.Select
                  ref={inputRef as React.RefObject<HTMLSelectElement>}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  disabled={isLoading}
                >
                  <option value="">-- Select --</option>
                  {INVOICED_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Form.Select>
              ) : isInternalNotesField ? (
                <div className="position-relative">
                  <Form.Control
                    ref={inputRef as React.RefObject<HTMLTextAreaElement>}
                    as="textarea"
                    rows={4}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder={`Enter ${fieldLabel.toLowerCase()}`}
                    disabled={isLoading}
                    style={{ paddingRight: value ? '2rem' : undefined }}
                  />
                  {value && !isLoading && (
                    <Button
                      variant="link"
                      size="sm"
                      className="position-absolute end-0 text-muted p-0 me-2"
                      onClick={() => {
                        setValue('');
                        inputRef.current?.focus();
                      }}
                      style={{ lineHeight: 1, fontSize: '1.2rem', top: '0.5rem' }}
                      tabIndex={-1}
                    >
                      <i className="bi bi-x-circle"></i>
                    </Button>
                  )}
                </div>
              ) : (
                <div className="position-relative">
                  <Form.Control
                    ref={inputRef as React.RefObject<HTMLInputElement>}
                    type="text"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder={`Enter ${fieldLabel.toLowerCase()}`}
                    disabled={isLoading}
                    style={{ paddingRight: value ? '2rem' : undefined }}
                  />
                  {value && !isLoading && (
                    <Button
                      variant="link"
                      size="sm"
                      className="position-absolute top-50 end-0 translate-middle-y text-muted p-0 me-2"
                      onClick={() => {
                        setValue('');
                        inputRef.current?.focus();
                      }}
                      style={{ lineHeight: 1, fontSize: '1.2rem' }}
                      tabIndex={-1}
                    >
                      <i className="bi bi-x-circle"></i>
                    </Button>
                  )}
                </div>
              )}
            </Form.Group>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={isLoading}>
          {isLoading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Saving...
            </>
          ) : (
            'Save'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EventEditModal;
